import { getAlignedRecordings } from '../../../stores/alignedRecordings';
import { getActiveSpeakersWithinTimeRange } from '../../activeSpeaker';
import { UPLOAD_SCREENSHARE_ID, ORIGINAL_VIDEO_ID } from './constants';
import { RecordingVideoType, VideoToSpeakerDetails } from '../../../stores/videoSpeakerDetailsStore';
import { getSpeakerDetailsForBroadcast } from '../../../stores/speakerDetailsStore';
import { getFontFamilyStyle, getFontLocationFromAssetMetadata } from '@/App/remotion/libs/font';
import { SpeakerWithDetails } from '@/context/TranscriptContext/TranscriptContextTypes';
import { Clip, ClipDeletes } from '@/domains/asset';
import { getDefaultFontProperties } from '@/libs/sharedAPI/constants';
import { SelectionElementType } from '@/App/remotion/components/Outlines/types';
import { isLiveEventRemotionWithPreviewAndAlignedStream } from '@/stores/clip';
import {
  getClipBroadcastId,
  isClipFromRecordingStudio,
  isClipFromUploads,
  isCustomUploadClip,
  isFullLengthClip
} from '@/libs/clipUtils';
import { isEntityScreenshare } from '@/libs/activityUtils';
import { speakersAnalysisStore } from '@/stores/speakersAnalysis/speakersAnalysis';
import { SpeakersAnalysis } from '@/stores/speakersAnalysis/speakersAnalysisTypes';

export const getVideoDetailsFromSpeaker = (speaker: SpeakerWithDetails) => {
  return {
    videoId: speaker.key,
    name: `Video ${speaker.key}`
  };
};

export const generateVideoIdSpeakerMapFromSpeakers = (
  speakersWithDetails: Record<string, SpeakerWithDetails>
): VideoToSpeakerDetails => {
  const videoIdToSpeakerKeyMap: VideoToSpeakerDetails = {};
  Object.values(speakersWithDetails ?? {}).forEach(speaker => {
    const { videoId } = getVideoDetailsFromSpeaker(speaker);
    videoIdToSpeakerKeyMap[videoId] = { speakerKey: speaker.key, videoType: RecordingVideoType.USER_FEED };
  });
  return videoIdToSpeakerKeyMap;
};

const getVideosTimeAndRangesForEventsAndUploads = ({
  clip
}: {
  clip: Clip;
}): ReturnType<typeof getVideosAndTimeRanges> => {
  const speakersWithDetails = getSpeakerDetailsForBroadcast(getClipBroadcastId(clip)!);

  const activeRanges = getActiveSpeakersWithinTimeRange(
    clip.asset_metadata.start,
    clip.asset_metadata.end,
    speakersWithDetails
  );

  const speakerIdToRangeMap: { [key: string]: { start: number; end: number }[] } = {};

  activeRanges.forEach(range => {
    if (!range) {
      return;
    }
    if (!speakerIdToRangeMap[range.speakerId]) {
      speakerIdToRangeMap[range.speakerId] = [];
    }
    speakerIdToRangeMap[range.speakerId].push({ start: range.start, end: range.end });
  });

  return Object.entries(speakersWithDetails).map(([_, speaker]) => {
    const { videoId } = getVideoDetailsFromSpeaker(speaker);

    return {
      videoId,
      ranges: speakerIdToRangeMap[speaker.key] ?? [],
      isScreenshare: false,
      isOriginalVideo: false
    };
  });
};

const getScreenshareVideoRanges = (
  screenshare_bounds: SpeakersAnalysis['screenshare_bounds']
): ReturnType<typeof getVideosAndTimeRanges> => {
  if (!screenshare_bounds || screenshare_bounds.length === 0) {
    return [];
  }

  const screenshareRanges = screenshare_bounds.map(bound => ({
    start: bound.start_time,
    end: bound.end_time
  }));

  return [
    {
      videoId: UPLOAD_SCREENSHARE_ID,
      ranges: screenshareRanges,
      isScreenshare: true,
      isOriginalVideo: false
    }
  ];
};

const getVideosTimeAndRangesForCustomUploadsClips = ({
  clip,
  speakersAnalysis
}: {
  clip: Clip;
  speakersAnalysis: SpeakersAnalysis;
}): ReturnType<typeof getVideosAndTimeRanges> => {
  const speakersWithDetails = getSpeakerDetailsForBroadcast(getClipBroadcastId(clip)!);
  const { speaker_mapping, time_analysis, times } = speakersAnalysis;

  const speakerKeyToRangeMap: { [key: string]: { start: number; end: number }[] } = {};

  // Build map of face_id to speaker
  const faceIdToSpeaker: { [faceId: number]: SpeakerWithDetails } = {};
  Object.entries(speaker_mapping).forEach(([speakerId, faceIds]) => {
    const speaker = Object.values(speakersWithDetails).find(s => s.id === speakerId);
    if (speaker) {
      faceIds.forEach(faceId => {
        faceIdToSpeaker[faceId] = speaker;
      });
    }
  });

  // Track ranges for each face_id
  const faceIdRanges: { [faceId: number]: { start: number; end: number } } = {};

  time_analysis.forEach((analysis, index) => {
    const currentTime = times[index];
    const nextTime = index < times.length - 1 ? times[index + 1] : clip.asset_metadata.end;

    analysis.face_positions.forEach(fp => {
      if (!faceIdRanges[fp.face_id]) {
        faceIdRanges[fp.face_id] = { start: currentTime, end: nextTime };
      } else {
        faceIdRanges[fp.face_id].end = nextTime;
      }
    });
  });

  // Convert face ranges to speaker ranges
  Object.entries(faceIdRanges).forEach(([faceId, range]) => {
    const speaker = faceIdToSpeaker[Number(faceId)];
    if (!speaker) {
      return;
    }

    if (!speakerKeyToRangeMap[speaker.key]) {
      speakerKeyToRangeMap[speaker.key] = [];
    }
    speakerKeyToRangeMap[speaker.key].push(range);
  });

  return Object.entries(speakersWithDetails).map(([_, speaker]) => {
    const { videoId } = getVideoDetailsFromSpeaker(speaker);

    return {
      videoId,
      ranges: speakerKeyToRangeMap[speaker.key] ?? [],
      isScreenshare: false,
      isOriginalVideo: false
    };
  });
};

// TODO @HiteshChoudhary0098 we should remove this function and use the getVideosAndTimeRangesForSegment function instead once we start supporting recording from multiple recordings
const getVideosAndTimeRangesFromAlignedStream = (
  broadcastId: string | null
): ReturnType<typeof getVideosAndTimeRanges> => {
  return getAlignedRecordings(broadcastId).map(recording => {
    const { stage_time_spans, id: videoId, entity } = recording;
    const isScreenshare = isEntityScreenshare(entity);

    const ranges: { start: number; end: number }[] = (stage_time_spans || []).map(times => ({
      start: times.start_time,
      end: times.end_time
    }));

    return {
      videoId,
      ranges,
      isScreenshare,
      isOriginalVideo: false
    };
  });
};
const getTimeRangesForOriginalVideo = ({ clip }: { clip: Clip }) => {
  return {
    videoId: ORIGINAL_VIDEO_ID,
    ranges: [{ start: clip.asset_metadata.start, end: clip.asset_metadata.end }],
    isScreenshare: false,
    isOriginalVideo: true
  };
};

export const getVideosAndTimeRanges = ({
  clip
}: {
  clip: Clip;
}): {
  videoId: string;
  ranges: { start: number; end: number }[];
  isScreenshare: boolean;
  isOriginalVideo: boolean;
}[] => {
  if (isLiveEventRemotionWithPreviewAndAlignedStream(clip)) {
    const videoRanges = getVideosAndTimeRangesFromAlignedStream(getClipBroadcastId(clip));
    videoRanges.push(getTimeRangesForOriginalVideo({ clip }));
    return videoRanges;
  }

  if (isClipFromUploads(clip) && isFullLengthClip(clip)) {
    const speakersAnalysis = speakersAnalysisStore.getSnapshot()[clip.id];
    if (speakersAnalysis?.screenshare_bounds && speakersAnalysis.screenshare_bounds.length > 0) {
      return [
        ...getScreenshareVideoRanges(speakersAnalysis.screenshare_bounds),
        getTimeRangesForOriginalVideo({ clip })
      ];
    }
  }

  if (isCustomUploadClip({ clip })) {
    const speakersAnalysis = speakersAnalysisStore.getSnapshot()[clip.id];
    const videos: ReturnType<typeof getVideosAndTimeRanges> = [];
    let shouldAddOriginalVideo = false;

    if (speakersAnalysis?.screenshare_bounds && speakersAnalysis.screenshare_bounds.length > 0) {
      shouldAddOriginalVideo = true;
      videos.push(...getScreenshareVideoRanges(speakersAnalysis.screenshare_bounds));
    }

    if (speakersAnalysis?.speaker_mapping && Object.keys(speakersAnalysis.speaker_mapping).length > 0) {
      shouldAddOriginalVideo = true;
      videos.push(...getVideosTimeAndRangesForCustomUploadsClips({ clip, speakersAnalysis }));
    } else if (shouldAddOriginalVideo) {
      // If there's screenshare but no speaker mapping, use fallback case
      videos.push(...getVideosTimeAndRangesForEventsAndUploads({ clip }));
    }

    if (shouldAddOriginalVideo) {
      videos.push(getTimeRangesForOriginalVideo({ clip }));
      return videos;
    }

    return [...getVideosTimeAndRangesForEventsAndUploads({ clip }), getTimeRangesForOriginalVideo({ clip })];
  }

  if (isClipFromRecordingStudio(clip)) {
    return getVideosAndTimeRangesFromAlignedStream(getClipBroadcastId(clip));
  }

  // Event and upload without facial rec or aligned streams
  return [...getVideosTimeAndRangesForEventsAndUploads({ clip }), getTimeRangesForOriginalVideo({ clip })];
};

export const getFlattenedDeletes = ({ clipDeletes, fps }: { clipDeletes: ClipDeletes; fps: number }) => {
  if (clipDeletes === undefined) {
    return [];
  }
  return Object.values(clipDeletes)
    .map(data => ({
      fromFrame: data.bounds[0] * fps,
      toFrame: data.bounds[1] * fps
    }))
    .filter(data => data.toFrame !== data.fromFrame)
    .sort((a, b) => a.fromFrame - b.fromFrame);
};

// This is required to adjust the frame of the main segment to account for the deletes
export const getAdjustedFrame = (frame: number, deletes: ReturnType<typeof getFlattenedDeletes>, start: number) => {
  let adjustment = 0;

  for (const deleteData of deletes) {
    if (deleteData.fromFrame >= frame) {
      break;
    }
    if (deleteData.toFrame <= frame) {
      adjustment += deleteData.toFrame - deleteData.fromFrame;
    }
  }

  return frame - adjustment - start;
};

// This is required to get the available ranges after the deletes
export const getAvailableRanges = (
  totalRange: [number, number],
  deletedRanges: ReturnType<typeof getFlattenedDeletes>
) => {
  // Extract the start and end of the total range
  const [totalStart, totalEnd] = totalRange;

  // Sort the deleted ranges by their start value
  deletedRanges.sort((a, b) => a.fromFrame - b.fromFrame);

  const remainingRanges: [number, number][] = [];

  // Track the end of the last deleted range
  let lastEnd = totalStart;

  // Loop through the deleted ranges and find gaps
  for (let i = 0; i < deletedRanges.length; i++) {
    const { fromFrame, toFrame } = deletedRanges[i];

    // If there is a gap before the current deleted range
    if (fromFrame > lastEnd) {
      remainingRanges.push([lastEnd, fromFrame]);
    }

    // Update the last end point to the end of the current deleted range
    lastEnd = Math.max(lastEnd, toFrame);
  }

  // If there is any remaining range after the last deleted range
  if (lastEnd < totalEnd) {
    remainingRanges.push([lastEnd, totalEnd]);
  }

  return remainingRanges;
};

export const getFontObject = ({ clip, fontTarget }: { clip: Clip; fontTarget: SelectionElementType }) => {
  const fontLocation = getFontLocationFromAssetMetadata(clip.asset_metadata, fontTarget);
  const fontName = getFontFamilyStyle(clip.id, fontLocation);

  if (fontName && fontLocation) {
    return { font: { location: fontLocation, name: fontName } };
  }
  const { font_location: location, font: name } = getDefaultFontProperties(true);
  return { font: { location, name } };
};
